import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import CustomImage from '../../components/customImage/CustomImage'
import styles from '../../styles/pages/News.module.scss'

import Grid from '../../components/grid/Grid'
import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'
import { useIsDesktop, useIsMobile } from '../../hooks/useIsDesktop'
import { useHasMounted } from '../../hooks/useHasMounted'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

import NewsPreviewItem, { NewsProps } from '../../components/news/News'

type Props = {
  articles: any
} & GenericPageLayoutProps

const News = (props: Props) => {
  const { setData } = useGlobalContentContext()
  const hasMounted = useHasMounted()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const isDesktop = useIsDesktop()
  const isMobile = useIsMobile()
  const slideMargin = 10

  const articles = props.articles

  const sort_order = articles.findIndex(
    (article: any) => article.slug == props.originalResponse.slug
  )

  function getArticles(start: number, end: number) {
    return [...articles, ...articles].slice(
      start,
      end < start ? end + articles.length : end
    )
  }

  const other_articles = getArticles(sort_order + 1, sort_order + 4)

  const date = new Date(props.originalResponse.pub_time)
  const formattedDate = `${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}.${String(date.getDate()).padStart(2, '0')}.${date
    .getFullYear()
    .toString()
    .slice(-2)}`

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            {props.originalResponse.category ? (
              <h1 className={styles.heroTitleWide}>
                <p>{props.originalResponse.category.title}</p>
                <p
                  dangerouslySetInnerHTML={{
                    __html: props.originalResponse.title,
                  }}
                ></p>
              </h1>
            ) : (
              <h1
                className={styles.heroTitleWide}
                dangerouslySetInnerHTML={{
                  __html: props.originalResponse.title,
                }}
              ></h1>
            )}
            <div className={styles.articleDate}>{formattedDate}</div>
          </div>
          
        </section>

        <section className={styles.newsContent}>
          <div className={styles.newsContentImageContainer}>
            <div className={styles.newsContentSticky}>
              <CustomImage src={props.originalResponse} layout="responsive" />
            </div>
          </div>
          <div>
            <div
              className={styles.newsContentText}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.detail
              )}
            ></div>

            {props.originalResponse.article_content_blocks.map(
              (content_block: any, index: number) => (
                <div key={index}>
                  {content_block.image && (
                    <div className={styles.contentBlockImage}>
                      <CustomImage src={content_block} layout="responsive" />
                    </div>
                  )}
                  {content_block.detail && (
                    <div
                      className={styles.newsContentBlockText}
                      dangerouslySetInnerHTML={createMarkup(
                        content_block.detail
                      )}
                    ></div>
                  )}
                </div>
              )
            )}
          </div>
        </section>

        <section className={`${styles.otherNews} news-button--low`}>
          <h2 className={styles.otherNewsTitle}>Other News</h2>
          {hasMounted && (
            <>
              {isDesktop && (
                <Grid columns={3}>
                  {other_articles.map((item: any, index: number) => (
                    <NewsPreviewItem
                      key={index}
                      item={item}
                      showDescription={true}
                      showPubDateTop={true}
                    />
                  ))}
                </Grid>
              )}
              {isMobile && (
                <Swiper
                  spaceBetween={slideMargin}
                  slidesPerView="auto"
                  slidesOffsetBefore={0}
                  loop={true}
                  navigation={true}
                  modules={[Navigation]}
                >
                  {other_articles.map((item: any, index: number) => (
                    <SwiperSlide
                      className={styles.swiperSlideContainer}
                      key={index}
                      virtualIndex={item.id}
                    >
                      <NewsPreviewItem
                        key={index}
                        item={item}
                        showDescription={true}
                        showPubDateTop={true}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}

type ParamsType = {
  params: {
    slug: string
  }
}

export async function getStaticProps({ params }: ParamsType) {
  const articlesRequest = await fetch(`${getAPI()}/news-articles/`)
  const allArticles = await articlesRequest.json()
  const articles = allArticles.filter(
    (article: any) => article.pub_status == 30
  )

  const article = articles.find((article: any) => article.slug == params.slug)

  if (!article) {
    return {
      notFound: true,
      revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
    }
  }

  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/news-articles/${params.slug}/`
  )

  return {
    props: {
      originalResponse: page.originalResponse,
      globalData: page.globalData,
      articles: articles,
      title: page.title,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export async function getStaticPaths() {
  const res = await fetch(`${getAPI()}/news-articles/`)
  const allArticles = await res.json()
  const articles = allArticles.filter(
    (article: any) => article.pub_status == 30
  )

  const paths = articles.map((article: any) => ({
    params: { slug: article.slug },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default News
